import { Stack } from '../components/core/Stack';
import { getDate } from '../data/getDate';
import React, { useEffect, useState } from 'react';
import { ValidationPanel } from '../components/panels/ValidationPanel';
import { GoalPanelDataSummary } from '../components/panels/GoalPanelDataSummary';
import { PanelDataSummary } from '../components/panels/PanelDataSummary';
import styled from '@emotion/styled';
import { currencyFormatter } from '../data/currency-formatter';
import { ValidatorTypes } from '../components/validators/ValidatorTypes';
import { useFormDispatch, useFormSelector } from '../redux/hooks';
import {
  getActivelyOwnedPropertiesCount,
  getCompletedValidation,
  getEndDateBalanceForUser,
  getEquity,
  getGoalMetForUser,
  getOwnedOrSoldPropertiesCount,
  getStartAndEndDate,
} from '../redux/timelineSelectors';
import { setAnimationCompleted } from '../redux/timelineSlice';
import { DEFAULT_START_DELAY } from '../components/IAnimatedProps';
import { AnimatedWrapFormItem } from '../components/AnimatedWrapFormItem';

const StackContainer = styled(Stack)`
  align-items: stretch;
`;

const StackContainColumn = styled(Stack)`
  > div {
    margin-bottom: 10px;
  }

  > div:last-of-type {
    margin-bottom: 0;
  }
`;

export function UserSummary() {
  const metMonthlyGoal = useFormSelector(getGoalMetForUser);
  const [startDate, endDate] = useFormSelector(getStartAndEndDate);
  const equity = useFormSelector(getEquity);
  const allOwnedProperties = useFormSelector(getOwnedOrSoldPropertiesCount);
  const ownedProperties = useFormSelector(getActivelyOwnedPropertiesCount);
  const balance = useFormSelector(getEndDateBalanceForUser);
  const [validationType, setValidationType] = useState<ValidatorTypes>(ValidatorTypes.Optional);
  const isValid = useFormSelector(getCompletedValidation);
  const dispatch = useFormDispatch();

  useEffect(() => {
    if (isValid !== ValidatorTypes.Optional) {
      setValidationType(isValid);
      dispatch(setAnimationCompleted(false));
    }
  }, [dispatch, isValid]);

  if (!startDate || !endDate) {
    return null;
  }

  return (
    <div>
      <ValidationPanel
        title={`Results: ${getDate(startDate)} - ${getDate(endDate)}`}
        padRight={false}
        validationType={validationType}
        forceIsValid={false}
      >
        <StackContainer direction="row" paddingLeft="0" paddingRight="0" paddingBottom="0">
          <Stack direction="row" paddingLeft="0" paddingRight="0" paddingBottom="0" marginBottom="0">
            <StackContainColumn direction="column" paddingLeft="0" paddingBottom="0">
              <AnimatedWrapFormItem>
                <PanelDataSummary title="Years to goal" data={(endDate.getFullYear() - startDate.getFullYear()).toString()} />
              </AnimatedWrapFormItem>
              <AnimatedWrapFormItem>
                <PanelDataSummary title="Properties equity" data={`$${currencyFormatter(equity)}`} />
              </AnimatedWrapFormItem>
              <AnimatedWrapFormItem>
                <PanelDataSummary title="End balance" data={`$${currencyFormatter(balance)}`} />
              </AnimatedWrapFormItem>
            </StackContainColumn>
            <StackContainColumn direction="column" paddingLeft="10px" paddingBottom="0">
              <AnimatedWrapFormItem delay={DEFAULT_START_DELAY}>
                <PanelDataSummary title="Goal acheived" data={metMonthlyGoal ? 'Yes' : 'No'} />
              </AnimatedWrapFormItem>
              <AnimatedWrapFormItem delay={DEFAULT_START_DELAY}>
                <PanelDataSummary title="Properties owned" data={allOwnedProperties.toString()} />
              </AnimatedWrapFormItem>
              <AnimatedWrapFormItem delay={DEFAULT_START_DELAY}>
                <PanelDataSummary title="Current properties" data={ownedProperties.toString()} />
              </AnimatedWrapFormItem>
            </StackContainColumn>
          </Stack>
          <GoalPanelDataSummary />
        </StackContainer>
      </ValidationPanel>
    </div>
  );
}
